import api from './api';

// ═══════════════════════════════════════════════════════════════════
//  Activation des notifications push côté navigateur
//
//  Séquence : enregistrer le service worker → demander l'autorisation
//  à l'utilisateur → créer l'abonnement → l'envoyer au serveur.
//
//  Rappel important : sur iPhone, rien de tout cela ne fonctionne tant
//  que le site n'a pas été ajouté à l'écran d'accueil. C'est une
//  restriction d'Apple, pas un défaut de ce code.
// ═══════════════════════════════════════════════════════════════════

// La clé VAPID arrive en base64 « URL-safe » ; l'API du navigateur
// attend un tableau d'octets. Conversion obligatoire.
const base64VersUint8 = (base64) => {
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const brut = window.atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
    const sortie = new Uint8Array(brut.length);
    for (let i = 0; i < brut.length; i += 1) sortie[i] = brut.charCodeAt(i);
    return sortie;
};

// Le navigateur sait-il faire du push ?
export const pushSupporte = () =>
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

// 'default' (jamais demandé) | 'granted' (autorisé) | 'denied' (refusé)
export const etatAutorisation = () =>
    (pushSupporte() ? Notification.permission : 'unsupported');

// Sur iOS, le push exige que le site tourne en mode "application",
// c'est-à-dire ajouté à l'écran d'accueil.
export const estIOS = () =>
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export const estModeApplication = () =>
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;

const enregistrerServiceWorker = async () => {
    const existant = await navigator.serviceWorker.getRegistration('/service-worker.js');
    if (existant) return existant;
    return navigator.serviceWorker.register('/service-worker.js');
};

/**
 * Active les notifications sur cet appareil.
 * Renvoie { ok, message } — jamais d'exception à gérer côté composant.
 */
export const activerPush = async () => {
    if (!pushSupporte()) {
        return { ok: false, message: "Ce navigateur ne gère pas les notifications." };
    }
    if (estIOS() && !estModeApplication()) {
        return {
            ok: false,
            message: "Sur iPhone, ajoutez d'abord le site à votre écran d'accueil (Partager → Sur l'écran d'accueil), puis réessayez depuis cette icône.",
        };
    }

    try {
        const autorisation = await Notification.requestPermission();
        if (autorisation !== 'granted') {
            return {
                ok: false,
                message: "Notifications refusées. Vous pouvez changer d'avis dans les réglages du navigateur.",
            };
        }

        const reponse = await api.get('/push/public-key');
        const cle = reponse.data && reponse.data.cle;
        if (!cle) {
            return { ok: false, message: 'Les notifications ne sont pas encore configurées sur le serveur.' };
        }

        const registration = await enregistrerServiceWorker();
        await navigator.serviceWorker.ready;

        // Un abonnement peut déjà exister (autorisation redonnée après un refus).
        let abonnement = await registration.pushManager.getSubscription();
        if (!abonnement) {
            abonnement = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: base64VersUint8(cle),
            });
        }

        await api.post('/push/subscribe', abonnement.toJSON());
        return { ok: true, message: 'Notifications activées sur cet appareil.' };
    } catch (err) {
        return {
            ok: false,
            message: "L'activation n'a pas abouti. Réessayez dans un instant.",
        };
    }
};

/**
 * Remet l'abonnement de cet appareil en base s'il en a disparu.
 *
 * Cas visé : le navigateur a conservé l'autorisation, mais l'abonnement a été
 * invalidé côté service de push. Le serveur supprime alors la ligne devenue
 * morte, et l'appareil cesse de recevoir sans que rien ne le signale — le
 * bandeau « Activer » ne s'affiche pas, puisque l'autorisation est toujours
 * accordée.
 *
 * Cette fonction est silencieuse et sans effet de bord : elle ne demande
 * jamais d'autorisation, et ne fait rien si elle n'en a pas déjà une.
 */
export const synchroniserPush = async () => {
    try {
        if (!pushSupporte() || Notification.permission !== 'granted') return false;

        const reponse = await api.get('/push/public-key');
        const cle = reponse.data && reponse.data.cle;
        if (!cle) return false;

        const registration = await enregistrerServiceWorker();
        await navigator.serviceWorker.ready;

        let abonnement = await registration.pushManager.getSubscription();
        if (!abonnement) {
            abonnement = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: base64VersUint8(cle),
            });
        }

        // L'enregistrement est idempotent côté serveur (ON CONFLICT sur
        // l'endpoint) : le réémettre à chaque ouverture ne crée pas de doublon.
        await api.post('/push/subscribe', abonnement.toJSON());
        return true;
    } catch (err) {
        return false;
    }
};

/** Désactive les notifications sur cet appareil. */
export const desactiverPush = async () => {
    try {
        const registration = await navigator.serviceWorker.getRegistration('/service-worker.js');
        const abonnement = registration && (await registration.pushManager.getSubscription());
        if (abonnement) {
            await api.delete('/push/unsubscribe', { data: { endpoint: abonnement.endpoint } });
            await abonnement.unsubscribe();
        }
        return { ok: true, message: 'Notifications désactivées sur cet appareil.' };
    } catch (err) {
        return { ok: false, message: 'La désactivation n\'a pas abouti.' };
    }
};